import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { submitBatteryC } from '@/services/batteryC';

// ============================================
// DATA
// ============================================

const STATE_ITEMS = [
  { q: 1, text: 'Я чувствую себя энергичным и полным сил' },
  { q: 2, text: 'Мне трудно сосредоточиться на чём-то одном' }, // inverted
  { q: 3, text: 'Я доволен(а) тем, как складываются дела' },
  { q: 4, text: 'Я чувствую тревогу или беспокойство' }, // inverted
  { q: 5, text: 'Я чувствую связь с людьми вокруг' },
  { q: 6, text: 'Мне кажется, что я застрял(а) на месте' }, // inverted
];

const STYLE_AXES = [
  { q: 1, label: 'Общение', A: 'Общительный — легко завожу разговоры', B: 'Сдержанный — предпочитаю наблюдать' },
  { q: 2, label: 'Конфликты', A: 'Прямой — говорю как есть', B: 'Избегающий — стараюсь сгладить' },
  { q: 3, label: 'Структура', A: 'Порядок — люблю план и систему', B: 'Гибкость — действую по ситуации' },
  { q: 4, label: 'Подход', A: 'Новизна — хочу пробовать новое', B: 'Углубление — хочу развить имеющееся' },
  { q: 5, label: 'Стресс', A: 'Действие — сразу берусь за дело', B: 'Анализ — сначала думаю, потом делаю' },
];

// ============================================
// TYPES
// ============================================

type Screen = 'intro' | 'state' | 'style' | 'anchors' | 'loading' | 'done';

interface Props {
  onComplete: () => void;
  onClose: () => void;
}

// ============================================
// MAIN COMPONENT
// ============================================

export function BatteryC({ onComplete, onClose }: Props) {
  const [screen, setScreen] = useState<Screen>('intro');
  const [error, setError] = useState<string | null>(null);
  const startTime = useRef(Date.now());

  // C1 state
  const [stateValues, setStateValues] = useState<Record<number, number>>({});

  // C2 style
  const [styleChoices, setStyleChoices] = useState<Record<number, 'A' | 'B'>>({});

  // C3 anchors
  const [anchor1, setAnchor1] = useState('');
  const [anchor2, setAnchor2] = useState('');

  const [result, setResult] = useState<{
    stateIndex: number;
    tieBreakApplied: boolean;
    quality: { valid: boolean; reason: string | null };
  } | null>(null);

  const handleSubmit = async () => {
    setScreen('loading');
    setError(null);

    const completionTimeSeconds = Math.round((Date.now() - startTime.current) / 1000);

    try {
      const res = await submitBatteryC({
        stateItems: STATE_ITEMS.map(item => ({
          question: item.q,
          value: stateValues[item.q] || 4,
        })),
        fcItems: STYLE_AXES.map(axis => ({
          question: axis.q,
          choice: styleChoices[axis.q] || 'A',
        })),
        anchors: [
          ...(anchor1 ? [{ question: 1, text: anchor1 }] : []),
          ...(anchor2 ? [{ question: 2, text: anchor2 }] : []),
        ],
        completionTimeSeconds,
      });

      setResult({
        stateIndex: res.stateIndex,
        tieBreakApplied: res.tieBreakApplied,
        quality: res.quality,
      });
      setScreen('done');
    } catch (err: unknown) {
      setError((err as Error).message);
      setScreen('anchors');
    }
  };

  // ============================================
  // RENDER
  // ============================================

  if (screen === 'intro') {
    return (
      <Wrapper>
        <div className="flex flex-col min-h-screen">
          {/* Header at top */}
          <div className="p-4 text-center">
            <h1 className="text-2xl font-bold text-white">Диагностика C</h1>
            <p className="text-primary-400 text-sm">Калибровка состояния</p>
          </div>

          {/* Large visual - people at foot of mountain */}
          <div className="flex-1 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-slate-800/40 via-indigo-900/30 to-primary-950" />
            <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-primary-950 to-transparent" />

            {/* Mountain visual */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative">
                {/* Mountain */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1 }}
                  className="text-8xl mb-4"
                >
                  🏔️
                </motion.div>

                {/* Group of people */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5, duration: 0.8 }}
                  className="absolute -bottom-12 left-1/2 -translate-x-1/2 text-3xl flex gap-2"
                >
                  <span className="opacity-70">👤</span>
                  <span className="opacity-80">👤</span>
                  <span>👤</span>
                  <span className="opacity-80">👤</span>
                  <span className="opacity-70">👤</span>
                </motion.div>
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute top-10 left-10 text-xl opacity-30">⚡</div>
            <div className="absolute top-16 right-12 text-sm opacity-20">✦</div>
            <div className="absolute top-28 left-20 text-xs opacity-25">✧</div>
          </div>

          {/* Bottom section with description and button */}
          <div className="p-6 text-center">
            <p className="text-primary-500 text-xs mb-2">90-120 секунд</p>
            <p className="text-primary-400 text-sm mb-6 max-w-xs mx-auto">
              Быстрая оценка вашего текущего состояния и стиля.
              Помогает уточнить профиль, если результаты Теста A были неоднозначны.
            </p>
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={() => { startTime.current = Date.now(); setScreen('state'); }}
              className="w-full max-w-xs py-3 rounded-xl bg-indigo-600 text-white font-medium"
            >
              Начать
            </motion.button>
            <button onClick={onClose} className="mt-4 text-primary-500 text-sm">Позже</button>
          </div>
        </div>
      </Wrapper>
    );
  }

  if (screen === 'loading') {
    return (
      <Wrapper>
        <div className="flex flex-col items-center justify-center min-h-screen">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
            className="w-12 h-12 rounded-full border-3 border-primary-700 border-t-indigo-400 mb-4"
          />
          <p className="text-primary-400 text-sm">Обработка...</p>
        </div>
      </Wrapper>
    );
  }

  if (screen === 'done') {
    return (
      <Wrapper>
        <div className="flex flex-col items-center justify-center min-h-screen p-6 text-center">
          <span className="text-5xl mb-4">✅</span>
          <h1 className="text-xl font-bold text-white mb-3">Калибровка завершена!</h1>

          {result && (
            <div className="space-y-3 mb-6 w-full max-w-xs">
              <div className="rounded-xl bg-primary-800/30 border border-primary-700/50 p-3">
                <p className="text-xs text-primary-500">Индекс состояния</p>
                <p className="text-2xl font-bold text-white">{result.stateIndex.toFixed(1)}<span className="text-sm text-primary-500">/7</span></p>
              </div>

              {result.tieBreakApplied && (
                <div className="rounded-xl bg-indigo-900/30 border border-indigo-800/30 p-3">
                  <p className="text-xs text-indigo-400">Профиль уточнён с помощью калибровки</p>
                </div>
              )}

              {!result.quality.valid && result.quality.reason && (
                <div className="rounded-xl bg-amber-900/20 border border-amber-800/30 p-3">
                  <p className="text-xs text-amber-400">{result.quality.reason}</p>
                </div>
              )}
            </div>
          )}

          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={onComplete}
            className="w-full max-w-xs py-3 rounded-xl bg-indigo-600 text-white font-medium"
          >
            Готово
          </motion.button>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {/* Progress */}
      <div className="p-4 border-b border-primary-800/50">
        <div className="flex items-center justify-between text-xs text-primary-500 mb-1">
          <span>Диагностика C</span>
          <span>{screen === 'state' ? '1' : screen === 'style' ? '2' : '3'}/3</span>
        </div>
        <div className="h-1.5 bg-primary-800 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-indigo-500 rounded-full"
            animate={{ width: screen === 'state' ? '33%' : screen === 'style' ? '66%' : '100%' }}
          />
        </div>
      </div>

      {error && (
        <div className="mx-4 mt-3 p-2 rounded-lg bg-red-900/30 border border-red-800/30">
          <p className="text-xs text-red-400 text-center">{error}</p>
        </div>
      )}

      <div className="p-4 overflow-y-auto flex-1">
        {/* C1: State */}
        {screen === 'state' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            <div className="p-3 rounded-xl bg-primary-800/30 border border-primary-700/50 mb-4">
              <p className="text-sm text-primary-300 mb-1">Оцени каждое утверждение:</p>
              <p className="text-xs text-primary-400">
                <span className="text-indigo-400 font-medium">1</span> = Совсем не про меня →{' '}
                <span className="text-indigo-400 font-medium">7</span> = Полностью про меня
              </p>
            </div>
            {STATE_ITEMS.map(item => (
              <div key={item.q} className="space-y-1">
                <p className="text-sm text-white">{item.text}</p>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5, 6, 7].map(v => (
                    <button
                      key={v}
                      onClick={() => setStateValues({ ...stateValues, [item.q]: v })}
                      className={`flex-1 py-2 rounded-lg text-xs font-medium border transition-colors ${
                        stateValues[item.q] === v
                          ? 'bg-indigo-900/40 border-indigo-700/50 text-indigo-300'
                          : 'bg-primary-800/30 border-primary-700/50 text-primary-500'
                      }`}
                    >{v}</button>
                  ))}
                </div>
              </div>
            ))}
            <div className="flex justify-between text-xs text-primary-600 px-1">
              <span>Совсем нет</span>
              <span>Полностью</span>
            </div>
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={() => setScreen('style')}
              disabled={Object.keys(stateValues).length < 6}
              className="w-full py-3 rounded-xl bg-indigo-600 text-white font-medium text-sm disabled:opacity-40"
            >
              Далее
            </motion.button>
          </motion.div>
        )}

        {/* C2: Style */}
        {screen === 'style' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            <p className="text-sm text-primary-300 mb-2">Что вам ближе?</p>
            {STYLE_AXES.map(axis => (
              <div key={axis.q} className="space-y-1">
                <p className="text-xs text-primary-500">{axis.label}</p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setStyleChoices({ ...styleChoices, [axis.q]: 'A' })}
                    className={`p-3 rounded-xl text-xs text-left border transition-colors ${
                      styleChoices[axis.q] === 'A'
                        ? 'bg-indigo-900/40 border-indigo-700/50 text-indigo-300'
                        : 'bg-primary-800/30 border-primary-700/50 text-primary-400'
                    }`}
                  >{axis.A}</button>
                  <button
                    onClick={() => setStyleChoices({ ...styleChoices, [axis.q]: 'B' })}
                    className={`p-3 rounded-xl text-xs text-left border transition-colors ${
                      styleChoices[axis.q] === 'B'
                        ? 'bg-indigo-900/40 border-indigo-700/50 text-indigo-300'
                        : 'bg-primary-800/30 border-primary-700/50 text-primary-400'
                    }`}
                  >{axis.B}</button>
                </div>
              </div>
            ))}
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={() => setScreen('anchors')}
              disabled={Object.keys(styleChoices).length < 5}
              className="w-full py-3 rounded-xl bg-indigo-600 text-white font-medium text-sm disabled:opacity-40"
            >
              Далее
            </motion.button>
          </motion.div>
        )}

        {/* C3: Anchors */}
        {screen === 'anchors' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            <p className="text-sm text-primary-300">Якоря смысла (опционально):</p>
            <div>
              <label className="text-xs text-primary-500 mb-1 block">Что даёт мне энергию?</label>
              <textarea
                value={anchor1}
                onChange={(e) => setAnchor1(e.target.value)}
                maxLength={160}
                rows={2}
                placeholder="Необязательно..."
                className="w-full bg-primary-800/50 border border-primary-700/50 rounded-xl p-3 text-white text-sm placeholder-primary-600 resize-none focus:outline-none focus:border-indigo-500/50"
              />
              <p className="text-xs text-primary-600 text-right">{anchor1.length}/160</p>
            </div>
            <div>
              <label className="text-xs text-primary-500 mb-1 block">Ради чего я здесь?</label>
              <textarea
                value={anchor2}
                onChange={(e) => setAnchor2(e.target.value)}
                maxLength={160}
                rows={2}
                placeholder="Необязательно..."
                className="w-full bg-primary-800/50 border border-primary-700/50 rounded-xl p-3 text-white text-sm placeholder-primary-600 resize-none focus:outline-none focus:border-indigo-500/50"
              />
              <p className="text-xs text-primary-600 text-right">{anchor2.length}/160</p>
            </div>
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={handleSubmit}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium text-sm"
            >
              Завершить калибровку
            </motion.button>
          </motion.div>
        )}
      </div>
    </Wrapper>
  );
}

function Wrapper({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-primary-950 safe-area-top safe-area-bottom flex flex-col">
      {children}
    </div>
  );
}
